<template>
	<section class="personal-settings-page">
		<header class="personal-settings-header">
			<h1 class="personal-settings-title">{{ $t('personal_settings') }}</h1>
			<p class="personal-settings-lead">{{ $t('personal_settings_lead') }}</p>
		</header>

		<div class="settings-cards">
			<article class="settings-card">
				<h2 class="settings-card-title">{{ $t('account') }}</h2>
				<p class="settings-card-hint">{{ $t('personal_settings_read_only') }}</p>
				<dl class="settings-dl">
					<div class="settings-dl-row">
						<dt>{{ $t('username') }}</dt>
						<dd>{{ displayUsername }}</dd>
					</div>
					<div class="settings-dl-row">
						<dt>{{ $t('email') }}</dt>
						<dd>{{ displayEmail }}</dd>
					</div>
					<div class="settings-dl-row">
						<dt>{{ $t('personal_settings_role') }}</dt>
						<dd>{{ $t(loggedUser?.type) }}</dd>
					</div>
				</dl>
			</article>

			<article class="settings-card settings-card-security">
				<h2 class="settings-card-title">{{ $t('change_password') }}</h2>
				<p class="settings-card-hint">{{ $t('change_password_hint') }}</p>
				<form class="password-form" @submit.prevent="submitPasswordChange">
					<label class="password-field">
						<span class="password-label">{{ $t('current_password') }}</span>
						<input
							v-model="passwordForm.currentPassword"
							type="password"
							autocomplete="current-password"
							:disabled="passwordChangeInProgress"
						/>
					</label>
					<label class="password-field">
						<span class="password-label">{{ $t('new_password') }}</span>
						<input
							v-model="passwordForm.newPassword"
							type="password"
							autocomplete="new-password"
							:disabled="passwordChangeInProgress"
						/>
						<span class="password-sub-hint">{{ $t('password_min_length') }}</span>
					</label>
					<label class="password-field">
						<span class="password-label">{{ $t('confirm_new_password') }}</span>
						<input
							v-model="passwordForm.confirmPassword"
							type="password"
							autocomplete="new-password"
							:disabled="passwordChangeInProgress"
						/>
					</label>
					<p v-if="passwordError" class="password-error">{{ passwordError }}</p>
					<Button
						class="change-password-btn"
						:disabled="passwordChangeInProgress"
						@click="submitPasswordChange"
					>
						{{ passwordChangeInProgress ? $t('loading') + '…' : $t('change_password') }}
					</Button>
				</form>
			</article>

			<article class="settings-card settings-card-privacy">
				<h2 class="settings-card-title">{{ $t('privacy') }}</h2>
				<p class="settings-card-hint">{{ $t('personal_settings_export_hint') }}</p>
				<div class="privacy-actions">
					<Button
						class="export-data-btn"
						:disabled="exportInProgress"
						@click="downloadMyData"
					>
						{{ exportInProgress ? $t('loading') + '…' : $t('Download my data') }}
					</Button>
					<p class="settings-card-hint privacy-divider-hint">{{ $t('personal_settings_privacy_hint') }}</p>
					<Button skin="accent" class="erasure-btn" @click="showErasureConfirm">
						{{ $t('Request deletion of my data') }}
					</Button>
				</div>
			</article>
		</div>
	</section>
</template>

<script>
import Swal from 'sweetalert2'
import { mapGetters } from 'vuex'
import Button from '@/modules/common/components/Button'
import privacyService from '@/modules/common/services/privacyService'
import authService from '@/modules/auth/services/authService'
import { PASSWORD_MIN_LENGTH } from '@/modules/auth/passwordRules'
import { Toast } from '@/modules/common/Toast'

export default {
	name: 'PersonalSettings',
	components: { Button },
	data() {
		return {
			exportInProgress: false,
			passwordChangeInProgress: false,
			passwordError: '',
			passwordForm: {
				currentPassword: '',
				newPassword: '',
				confirmPassword: '',
			},
		}
	},
	computed: {
		...mapGetters(['loggedUser']),
		displayUsername() {
			const u = this.loggedUser
			if (!u) return '—'
			return u.username || '—'
		},
		displayEmail() {
			const u = this.loggedUser
			if (!u || !u.email) return '—'
			return u.email
		}
	},
	methods: {
		validatePasswordForm() {
			const { currentPassword, newPassword, confirmPassword } = this.passwordForm
			if (!currentPassword) return this.$t('current_password_required')
			if (newPassword.length < PASSWORD_MIN_LENGTH) return this.$t('password_min_length')
			if (newPassword !== confirmPassword) return this.$t('passwords_do_not_match')
			if (newPassword === currentPassword) return this.$t('new_password_same_as_current')
			return ''
		},
		async submitPasswordChange() {
			if (this.passwordChangeInProgress) return
			this.passwordError = this.validatePasswordForm()
			if (this.passwordError) return
			this.passwordChangeInProgress = true
			try {
				await authService.changePassword(this.passwordForm.currentPassword, this.passwordForm.newPassword)
				this.passwordForm = { currentPassword: '', newPassword: '', confirmPassword: '' }
				new Toast({ message: this.$t('password_changed_successfully'), type: 'success', duration: 4000 })
			} catch (err) {
				const codeToMessage = {
					CURRENT_PASSWORD_INCORRECT: this.$t('current_password_incorrect'),
					PASSWORD_SAME_AS_CURRENT: this.$t('new_password_same_as_current'),
					PASSWORD_TOO_SHORT: this.$t('password_min_length'),
					CURRENT_PASSWORD_REQUIRED: this.$t('current_password_required'),
					TOO_MANY_PASSWORD_CHANGES: this.$t('change_password_too_many'),
				}
				// Errors without a validation code are already toasted by httpService
				if (err && err.validationCode) {
					this.passwordError = codeToMessage[err.validationCode] || this.$t('change_password_failed')
				}
			} finally {
				this.passwordChangeInProgress = false
			}
		},
		async downloadMyData() {
			if (this.exportInProgress) return
			this.exportInProgress = true
			try {
				const data = await privacyService.exportMyData()
				if (data === undefined) return
				if (!data || typeof data !== 'object') {
					new Toast({ message: this.$t('data_export_failed'), type: 'error', duration: 5000 })
					return
				}
				const name = `buzzztv-my-data-${new Date().toISOString().slice(0, 10)}.json`
				const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json;charset=utf-8' })
				const url = URL.createObjectURL(blob)
				const a = document.createElement('a')
				a.href = url
				a.download = name
				a.rel = 'noopener'
				document.body.appendChild(a)
				a.click()
				document.body.removeChild(a)
				URL.revokeObjectURL(url)
				new Toast({ message: this.$t('data_exported_successfully'), type: 'success', duration: 4000 })
			} catch (_) {
				// httpService already toasts API errors
			} finally {
				this.exportInProgress = false
			}
		},
		showErasureConfirm() {
			Swal.fire({
				title: this.$t('Request deletion of my data'),
				text: this.$t('Request deletion of my data - confirm'),
				icon: 'info',
				showCancelButton: true,
				confirmButtonText: this.$t('OK'),
				cancelButtonText: this.$t('cancel'),
				reverseButtons: true,
				focusCancel: true,
				customClass: {
					popup: 'erasure-confirm-popup',
					title: 'erasure-confirm-title',
					htmlContainer: 'erasure-confirm-body',
					confirmButton: 'erasure-confirm-btn',
					cancelButton: 'erasure-confirm-cancel'
				},
				buttonsStyling: true
			}).then((result) => {
				if (result.isConfirmed) this.submitErasureRequest()
			})
		},
		async submitErasureRequest() {
			try {
				await privacyService.requestDataDeletion({ source: 'portal' })
				new Toast({ message: this.$t('Request deletion of my data - success'), type: 'success', duration: 5000 })
			} catch (err) {
				new Toast({ message: err.message || this.$t('Request deletion of my data - error'), type: 'error', duration: 5000 })
			}
		}
	}
}
</script>

<style lang="scss" scoped>
.personal-settings-page {
	max-width: 640px;
	padding: 24px 28px 48px;
	margin: 0 auto;
}

.personal-settings-header {
	margin-bottom: 28px;
}

.personal-settings-title {
	font-size: 1.5rem;
	font-weight: 600;
	color: #2c3e50;
	margin: 0 0 8px;
}

.personal-settings-lead {
	margin: 0;
	font-size: 14px;
	color: #7f8c8d;
	line-height: 1.45;
}

.settings-cards {
	display: flex;
	flex-direction: column;
	gap: 20px;
}

.settings-card {
	background: #fff;
	border-radius: 12px;
	border: 1px solid rgba(0, 0, 0, 0.06);
	box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
	padding: 20px 22px;
}

.settings-card-title {
	font-size: 15px;
	font-weight: 600;
	color: #2c3e50;
	margin: 0 0 8px;
}

.settings-card-hint {
	font-size: 13px;
	color: #7f8c8d;
	line-height: 1.45;
	margin: 0 0 16px;
}

.settings-dl {
	margin: 0;
}

.settings-dl-row {
	display: grid;
	grid-template-columns: 120px 1fr;
	gap: 12px 16px;
	padding: 10px 0;
	border-bottom: 1px solid #ecf0f1;
	font-size: 14px;

	&:last-child {
		border-bottom: none;
	}

	dt {
		margin: 0;
		color: #95a5a6;
		font-weight: 500;
	}

	dd {
		margin: 0;
		color: #2c3e50;
		word-break: break-word;
	}
}

.password-form {
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: 14px;
}

.password-field {
	display: flex;
	flex-direction: column;
	gap: 6px;
	width: 100%;
	max-width: 320px;

	input {
		padding: 9px 12px;
		border: 1px solid #d5dbdb;
		border-radius: 8px;
		font-size: 14px;
		color: #2c3e50;
		transition: border-color 0.2s;

		&:focus {
			outline: none;
			border-color: #00afd4;
		}

		&:disabled {
			background: #f6f8f8;
			color: #95a5a6;
		}
	}
}

.password-label {
	font-size: 13px;
	font-weight: 500;
	color: #95a5a6;
}

.password-sub-hint {
	font-size: 12px;
	color: #b2babb;
}

.password-error {
	margin: 0;
	font-size: 13px;
	color: #e74c3c;
}

.change-password-btn {
	margin: 4px 0 0 !important;
}

.privacy-actions {
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: 12px;
}

.privacy-divider-hint {
	margin: 8px 0 0 !important;
	padding-top: 12px;
	border-top: 1px solid #ecf0f1;
	width: 100%;
}

.settings-card-privacy .erasure-btn,
.settings-card-privacy .export-data-btn {
	margin-top: 0;
}
</style>

<style lang="scss">
.erasure-confirm-popup {
	max-width: 420px;
	border-radius: 12px;
	box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
	padding: 0 0 24px;
}
.erasure-confirm-title {
	font-size: 18px;
	font-weight: 600;
	color: #2c3e50;
	margin-bottom: 0;
	padding: 24px 24px 0;
}
.erasure-confirm-body {
	padding: 16px 24px 8px;
	text-align: left;
	font-size: 14px;
	line-height: 1.5;
	color: #5d6d7e;
}
.erasure-confirm-btn {
	padding: 10px 24px;
	border-radius: 8px;
	font-weight: 500;
}
.erasure-confirm-cancel {
	padding: 10px 24px;
	border-radius: 8px;
}
</style>
